import { doLogout } from '@/state/reducers/authSlice';

import HelperHighlightManager from '@/components/helper/HelperHighlightManager';
import HelperLauncher from '@/components/helper/HelperLauncher';
import {
    getPlatformLogo,
    isGlobalBlockPlatform,
    isOexPlatform,
    isPagoFacilPlatform,
    isPaytechPlatform,
    isWolfBTCPlatform,
    isXellarPaymentsPlatform,
    shouldUseNopBalance
} from '@/helpers/environmentHelper';
import { addToTopRight } from '@/helpers/mosaicHelper';
import { logToServer } from '@/services/LogService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { changeBalanceAccount } from '@/state/reducers/balanceSlice';
import { closeHelper, selectHelperOpened } from '@/state/reducers/helperSlice';
import {
    selectSelectedXplorSpotWorkspaceTab,
    selectSelectedXplorTraderWorkspaceTab,
    selectXplorSpotWorkspaceTabs,
    selectXplorTraderWorkspaceTabs,
    setXplorSpotWorkspace,
    setXplorSpotWorkspaceTabLayout,
    setXplorTraderWorkspace,
    setXplorTraderWorkspaceTabLayout
} from '@/state/reducers/workspaceSlice';
import history from '@/utils/history';
import { useDisclosure, UseDisclosureReturn } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import { UseSizeReturn } from '@/utils/hooks/useSize';
import { tabIcon as xplorSpotTabIcon, tabList as xplorSpotTabList } from '@/utils/hooks/useXplorSpotLayout';
import { xplorTraderTabIcon, xplorTraderTabList } from '@/utils/hooks/useXplorTraderLayout';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import cn from 'classnames';
import { Fragment, useCallback, useMemo } from 'react';
import { LuLayout } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';
import HelperModal from '../modal/HelperModal';
import SettingsModal from '../modal/SettingsModal';
import AddModule from './AddModule';
import DesktopNavbar, { MenuButton } from './DesktopNavbar';
import LiteMobileNavbar from './lite/LiteMobileNavbar';
import MobileDrawer from './MobileDrawer';
import MobileNavbar from './MobileNavbar';
import PortalMobileNavbar from './portal/PortalMobileNavbar';
import { MenuItemButton, WorkspaceDropdown } from './WorkspaceDropdown';

const xplorspot_logo = window.config.logo;
const lite_logo = window.config.lite_logo;
const xplortrader_logo = window.config.trader_logo;

export const onLogout = async (credentials, dispatch) => {
    try {
        if (credentials) {
            logToServer('info', `User ${credentials?.username} has manually logged out using navbar.`);
            await dispatch(doLogout({ credentials }) as any).unwrap();
        }
    } catch (rejectedValueOrSerializedError) {
        //
    }
    history.push('/login');
};

export const onUserChange = async (credentials, dispatch) => {
    if (credentials && !shouldUseNopBalance) await dispatch(changeBalanceAccount({ credentials }));
};

interface NavbarProps {
    size?: UseSizeReturn;
    settings?: UseDisclosureReturn;
}

export default function Navbar({ size, settings }: NavbarProps) {
    const pathname = window.location.pathname;

    const location = useLocation();

    const lang = useLanguage();
    const dispatch = useAppDispatch();

    const xplorSpotWorkspace = useAppSelector(selectSelectedXplorSpotWorkspaceTab);
    const xplorSpotWorkspaces = useAppSelector(selectXplorSpotWorkspaceTabs);
    const xplorTraderWorkspace = useAppSelector(selectSelectedXplorTraderWorkspaceTab);
    const xplorTraderWorkspaces = useAppSelector(selectXplorTraderWorkspaceTabs);
    const isHelperOpened = useAppSelector(selectHelperOpened);

    const activeXplorSpotId = useMemo(() => xplorSpotWorkspace?.id, [xplorSpotWorkspace]);
    const activeXplorTraderId = useMemo(() => xplorTraderWorkspace?.id, [xplorTraderWorkspace]);

    const activeId = useMemo(() => {
        if (pathname.includes('/trader')) {
            return activeXplorTraderId;
        } else {
            return activeXplorSpotId;
        }
    }, [activeXplorSpotId, activeXplorTraderId]);

    const workspace = useMemo(() => {
        if (pathname.includes('/trader')) {
            return xplorTraderWorkspace;
        } else {
            return xplorSpotWorkspace;
        }
    }, [pathname, xplorSpotWorkspace, xplorTraderWorkspace]);

    const workspaces = useMemo(() => {
        if (pathname.includes('/trader')) {
            return xplorTraderWorkspaces;
        } else {
            return xplorSpotWorkspaces;
        }
    }, [xplorSpotWorkspaces, xplorTraderWorkspace]);

    const setXplorSpotWorkspaceCallback = useCallback(
        (currentNode) => {
            dispatch(setXplorSpotWorkspaceTabLayout({ id: 'current', layout: currentNode, screen: 'desktop' }));
        },
        [dispatch]
    );

    const setXplorTraderWorkspaceCallback = useCallback(
        (currentNode) => {
            dispatch(setXplorTraderWorkspaceTabLayout({ id: 'current', layout: currentNode, screen: 'desktop' }));
        },
        [dispatch]
    );

    const drawer = useDisclosure(false);
    const { mobile, tablet } = useScreenSizes();

    const isMobile = useMemo(() => mobile || tablet, [mobile, tablet]);

    const { isXplorSpot, isXplorSpotLite, isXplorPortal, isXplorTrader } = useMemo(() => {
        return {
            isXplorSpot: location.pathname === '/' || location.pathname === '',
            isXplorSpotLite: location.pathname.includes('/lite'),
            isXplorPortal: location.pathname.includes('/portal'),
            isXplorTrader: location.pathname.includes('/trader')
        };
    }, [location]);

    const imageSrc = useMemo(() => {
        if (isXplorSpot) {
            return xplorspot_logo;
        } else if (isXplorSpotLite) {
            return lite_logo;
        } else if (isXplorPortal) {
            return getPlatformLogo();
        } else if (isXplorTrader) {
            return xplortrader_logo;
        } else {
            return '';
        }
    }, [isXplorSpot, isXplorSpotLite, isXplorPortal, isXplorTrader]);

    return (
        <header
            data-onboarding="top-bar"
            className={cn('flex justify-center z-50 min-h-[57.5px] navbar', {
                'absolute lg:fixed lg:top-[3px] lg:left-0 lg:pl-[260px]': isXplorPortal
            })}>
            <div
                className={cn(
                    'flex w-full justify-between relative lg:items-center gap-3 lg:px-3 p-1 border-brand-border border-b-[1.5px] lg:border-b-[3px] border-r-[1.5px] lg:border-r-[3px] border-l-[1.5px] lg:border-l-0 lg:h-[63px] bg-brand-background',
                    {
                        'px-4': !isMobile,
                        'border-l-[1.5px] lg:border-l-[3px]': isXplorSpotLite
                        // '!border-x-0 lg:!border-r-[3px]': isXplorPortal
                    }
                )}>
                <div className="flex flex-row items-center gap-3" data-onboarding="workspace-controls">
                    {!isMobile && (isXplorSpot || isXplorTrader) && (
                        <Fragment>
                            <WorkspaceDropdown
                                label={lang.workspace.workspace}
                                position="left"
                                workspaces={workspaces}
                                button={
                                    <MenuButton
                                        className="!min-w-40 !max-w-48"
                                        label={workspace?.name}
                                        Icon={LuLayout}
                                    />
                                }>
                                <MenuItemButton
                                    id={workspaces[0].id}
                                    label={workspaces[0].name}
                                    isActive={activeId === workspaces[0].id}
                                    onClick={() => {
                                        if (pathname.includes('/trader')) {
                                            dispatch(setXplorTraderWorkspace(workspaces[0].id));
                                        } else {
                                            dispatch(setXplorSpotWorkspace(workspaces[0].id));
                                        }
                                    }}
                                    isDefault
                                />
                                {workspaces.slice(1).map((workspace, i) => {
                                    return (
                                        <MenuItemButton
                                            key={i}
                                            id={workspace.id}
                                            label={workspace.name}
                                            isActive={activeId === workspace.id}
                                            onClick={() => {
                                                if (pathname.includes('/trader')) {
                                                    dispatch(setXplorTraderWorkspace(workspace.id));
                                                } else {
                                                    dispatch(setXplorSpotWorkspace(workspace.id));
                                                }
                                            }}
                                            isOnlyWorkspace={workspaces.length === 1}
                                        />
                                    );
                                })}
                            </WorkspaceDropdown>
                            <AddModule
                                tabIcon={pathname.includes('trader') ? xplorTraderTabIcon : xplorSpotTabIcon}
                                tabList={pathname.includes('trader') ? xplorTraderTabList : xplorSpotTabList}
                                platform={pathname.includes('trader') ? 'xplortrader' : 'xplorspot'}
                                onClick={(tab) => {
                                    addToTopRight(workspace?.layout.desktop, tab, (currentNode) => {
                                        if (pathname.includes('trader')) {
                                            setXplorTraderWorkspaceCallback(currentNode);
                                        } else {
                                            setXplorSpotWorkspaceCallback(currentNode);
                                        }
                                    });
                                }}
                                isIconOnly
                            />
                        </Fragment>
                    )}
                    {/* <EnvironmentBadge /> */}
                    {isMobile && (
                        <div className="flex flex-row h-full items-center gap-3">
                            {!(
                                isPaytechPlatform ||
                                isWolfBTCPlatform ||
                                isGlobalBlockPlatform ||
                                isXellarPaymentsPlatform ||
                                isPagoFacilPlatform ||
                                isOexPlatform
                            ) && <img className="block h-auto max-h-10 xsm:max-h-12 xsm:h-12" src={imageSrc} />}
                            {(isPaytechPlatform ||
                                isWolfBTCPlatform ||
                                isGlobalBlockPlatform ||
                                isXellarPaymentsPlatform ||
                                isPagoFacilPlatform ||
                                isOexPlatform) && (
                                <img className="block h-auto max-h-8 xs:max-h-10 xs:h-10 pl-1" src={imageSrc} />
                            )}
                        </div>
                    )}
                    {isPaytechPlatform && (
                        <div className="text-xs text-brand-module-title hidden xsm:block">Powered by GCEX</div>
                    )}
                </div>
                {(isXplorSpot || isXplorTrader) && <MobileNavbar drawerDisclosure={drawer} />}
                {isXplorSpotLite && <LiteMobileNavbar drawerDisclosure={drawer} />}
                {isXplorPortal && <PortalMobileNavbar drawerDisclosure={drawer} />}
                <DesktopNavbar isPortal={isXplorPortal} />
            </div>
            <MobileDrawer settingsDisclosure={settings} drawerDisclosure={drawer} />
            <HelperHighlightManager />
            <HelperModal opened={isHelperOpened} onClose={() => dispatch(closeHelper())} />
            {(isXplorSpot || isXplorTrader) && !isHelperOpened && <HelperLauncher />}
            {settings && <SettingsModal opened={settings[0]} handlers={settings[1]} />}
        </header>
    );
}
